import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  User,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';

interface JournalEntryLine {
  id: string;
  accountId: string;
  description?: string | null;
  debit: number;
  credit: number;
  account: {
    id: string;
    code: string;
    name: string;
    type: string;
  };
  customerId?: string | null;
  partnerId?: string | null;
  customer?: {
    firstName: string;
    lastName: string;
  } | null;
  partner?: {
    id: string;
    companyName: string;
  } | null;
}

export function JournalEntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('accounting');
  const { t: tCommon } = useTranslation('common');

  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const { data: entry, isLoading } = trpc.journalEntry.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const postMutation = trpc.journalEntry.post.useMutation({
    onSuccess: () => {
      utils.journalEntry.byId.invalidate(id);
      utils.journalEntry.list.invalidate();
      utils.journalEntry.stats.invalidate();
      utils.account.invalidate();
      utils.financialReports.invalidate();
      setIsPostDialogOpen(false);
    },
  });

  const voidMutation = trpc.journalEntry.void.useMutation({
    onSuccess: () => {
      utils.journalEntry.byId.invalidate(id);
      utils.journalEntry.list.invalidate();
      utils.journalEntry.stats.invalidate();
      utils.account.invalidate();
      utils.financialReports.invalidate();
      setIsVoidDialogOpen(false);
    },
  });

  const deleteMutation = trpc.journalEntry.delete.useMutation({
    onSuccess: () => {
      navigate('/travel/accounting');
    },
  });

  const handlePost = () => {
    // postedById is derived server-side from the authenticated session.
    postMutation.mutate({ id: id! });
  };

  const handleVoid = () => {
    if (voidReason.trim()) {
      voidMutation.mutate({ id: id!, reason: voidReason });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate(id!);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-muted-foreground text-lg font-medium">{t('detail.entryNotFound')}</p>
        <Button variant="link" onClick={() => navigate('/travel/accounting')}>
          {t('backToAccounting')}
        </Button>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700',
    PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
    POSTED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/travel/accounting')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-mono text-2xl font-bold">{entry.entryNumber}</h1>
              <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[entry.status]}`}>
                {entry.status}
              </span>
            </div>
            <p className="text-muted-foreground">{entry.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {entry.status === 'DRAFT' && (
            <>
              <Button
                variant="outline"
                className="h-8"
                onClick={() => navigate(`/travel/accounting/journal-entries/${id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                {t('detail.edit')}
              </Button>
              <Button
                variant="destructive"
                className="h-8"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('detail.delete')}
              </Button>
              <Button className="h-8" onClick={() => setIsPostDialogOpen(true)}>
                <CheckCircle className="mr-2 h-4 w-4" />
                {t('detail.postEntry')}
              </Button>
            </>
          )}
          {entry.status === 'POSTED' && (
            <Button variant="destructive" className="h-8" onClick={() => setIsVoidDialogOpen(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              {t('detail.voidEntry')}
            </Button>
          )}
        </div>
      </div>

      {/* Entry Details */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="border-border bg-card border p-6 lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold">{t('detail.entryDetails')}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <Calendar className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="text-muted-foreground text-sm">{t('detail.date')}</p>
                <p className="font-medium">{new Date(entry.date).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FileText className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="text-muted-foreground text-sm">{t('detail.referenceType')}</p>
                <p className="font-medium">{entry.referenceType || 'Manual'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="text-muted-foreground text-sm">{t('detail.createdBy')}</p>
                <p className="font-medium">{entry.createdById ?? '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="text-muted-foreground text-sm">{t('detail.createdAt')}</p>
                <p className="font-medium">{new Date(entry.createdAt).toLocaleString()}</p>
              </div>
            </div>
            {entry.postedById && (
              <>
                <div className="flex items-center gap-3">
                  <User className="text-muted-foreground h-5 w-5" />
                  <div>
                    <p className="text-muted-foreground text-sm">{t('detail.postedBy')}</p>
                    <p className="font-medium">{entry.postedById}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="text-muted-foreground h-5 w-5" />
                  <div>
                    <p className="text-muted-foreground text-sm">{t('detail.postedAt')}</p>
                    <p className="font-medium">
                      {entry.postedAt ? new Date(entry.postedAt).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="border-border bg-card border p-6">
          <h3 className="mb-4 text-lg font-semibold">{t('detail.entryTotals')}</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('detail.totalDebits')}</span>
              <span className="font-mono text-xl font-bold">
                {formatCurrency(entry.totalDebit, entry.currency)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('detail.totalCredits')}</span>
              <span className="font-mono text-xl font-bold">
                {formatCurrency(entry.totalCredit, entry.currency)}
              </span>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('detail.currency')}</span>
                <span className="font-medium">{entry.currency}</span>
              </div>
            </div>
            {entry.status === 'POSTED' && (
              <div className="mt-4 border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                {t('detail.postedMessage')}
              </div>
            )}
            {entry.status === 'VOIDED' && (
              <div className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {t('detail.voidedMessage')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Journal Lines */}
      <div className="border-border bg-card border">
        <div className="border-b p-6">
          <h3 className="text-lg font-semibold">
            {t('detail.journalLines')} ({entry.lines.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">#</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('detail.account')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  {t('detail.description')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium">{t('detail.debit')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium">{t('detail.credit')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entry.lines.map((line: JournalEntryLine, index: number) => (
                <tr key={line.id} className="hover:bg-muted/30">
                  <td className="text-muted-foreground px-4 py-3 text-sm">{index + 1}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/travel/accounting/accounts/${line.account.id}`}
                      className="hover:underline"
                    >
                      <span className="font-mono text-sm">{line.account.code}</span>
                      <span className="ml-2 text-sm">{line.account.name}</span>
                    </Link>
                    <span
                      className={`ml-2 px-2 py-0.5 text-xs ${
                        line.account.type === 'ASSET'
                          ? 'bg-blue-100 text-blue-700'
                          : line.account.type === 'LIABILITY'
                            ? 'bg-red-100 text-red-700'
                            : line.account.type === 'EQUITY'
                              ? 'bg-purple-100 text-purple-700'
                              : line.account.type === 'INCOME'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {line.account.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {line.description || '-'}
                    {(line.customer || line.partner) && (
                      <p className="text-muted-foreground text-xs">
                        {line.customer
                          ? `Customer: ${line.customer.firstName} ${line.customer.lastName}`
                          : `Partner: ${line.partner?.companyName}`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {line.debit > 0 ? formatCurrency(line.debit, entry.currency) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {line.credit > 0 ? formatCurrency(line.credit, entry.currency) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-medium">
              <tr>
                <td className="px-4 py-3" colSpan={3}>
                  <span className="text-sm">{t('detail.totals')}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(entry.totalDebit, entry.currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(entry.totalCredit, entry.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Post Confirmation Dialog */}
      <Dialog open={isPostDialogOpen} onOpenChange={setIsPostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.postTitle')}</DialogTitle>
            <DialogDescription>{t('detail.postMessage')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted border-border space-y-2 border p-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('detail.entryNumber')}</span>
                <span className="font-mono">{entry.entryNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('detail.date')}</span>
                <span>{new Date(entry.date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('detail.totalAmount')}</span>
                <span className="font-mono">
                  {formatCurrency(entry.totalDebit, entry.currency)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsPostDialogOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button className="h-8" onClick={handlePost} disabled={postMutation.isPending}>
              {postMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('detail.postEntry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog open={isVoidDialogOpen} onOpenChange={setIsVoidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.voidTitle')}</DialogTitle>
            <DialogDescription>{t('detail.voidMessage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {t('detail.voidWarning')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="voidReason">{t('detail.voidReason')}</Label>
              <Input
                id="voidReason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('voidReasonPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsVoidDialogOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleVoid}
              disabled={voidMutation.isPending || !voidReason.trim()}
            >
              {voidMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('detail.voidEntry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.deleteTitle')}</DialogTitle>
            <DialogDescription>{t('detail.deleteMessage')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsDeleteDialogOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('detail.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
